import { isPublicPath } from '@/shared/constants/publicRoutes';

import { salvarBlobComoArquivo } from './browserActions';
import { limparMarcaSessaoInvalida, marcarSessaoInvalida, removeLegacyAuthCookie } from './cookies';
import { getStoredWorkspaceId, removeStoredWorkspaceId } from './workspace';
import { headersDeContextoAuditoria } from './contextoAuditoria';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8081/api';

let refreshPromise: Promise<boolean> | null = null;
let failedRefreshCount = 0;
const MAX_REFRESH_ATTEMPTS = 2;

const AUTO_REFRESH_INTERVAL = 10 * 60 * 1000;
/** Piso entre renovações disparadas por foco — ver `renovarAoVoltarParaAba`. */
const MIN_INTERVALO_ENTRE_RENOVACOES = 5 * 60 * 1000;
let refreshIntervalId: NodeJS.Timeout | null = null;
let ultimaRenovacaoEm = 0;

const SHARED_REDIRECT_STORAGE_KEY = 'sharedItemRedirect';

const WS_RECOVERY_ATTEMPTS_KEY = 'wsRecoveryAttempts';
const WS_RECOVERY_MAX_ATTEMPTS = 3;

let redirecionandoParaLogin = false;

const irParaLogin = (destino: string): void => {
  redirecionandoParaLogin = true;
  window.location.href = destino;
};

const redirectToLoginIfNeeded = (): void => {
  if (typeof window === 'undefined') return;
  if (redirecionandoParaLogin) return;

  const currentPath = window.location.pathname;
  if (
    currentPath === '/login' ||
    currentPath === '/compartilhado/login' ||
    isPublicPath(currentPath)
  ) {
    return;
  }

  const sharedItemMatch = currentPath.match(/^\/compartilhado\/([^/]+)\/([^/]+)/);
  if (sharedItemMatch && sharedItemMatch[1] !== 'login') {
    const fullPath = currentPath + window.location.search;
    try {
      window.sessionStorage.setItem(SHARED_REDIRECT_STORAGE_KEY, fullPath);
    } catch {}
    const params = new URLSearchParams({
      module: sharedItemMatch[1],
      id: sharedItemMatch[2],
      redirect: fullPath,
    });
    irParaLogin(`/compartilhado/login?${params.toString()}`);
    return;
  }

  let storedRedirect: string | null = null;
  try {
    storedRedirect = window.sessionStorage.getItem(SHARED_REDIRECT_STORAGE_KEY);
  } catch {}

  const notifMatch = currentPath.match(/^\/notificacoes\/([^/]+)/);
  if (notifMatch && storedRedirect) {
    const fullPath = currentPath + window.location.search;
    try {
      window.sessionStorage.setItem(SHARED_REDIRECT_STORAGE_KEY, fullPath);
    } catch {}
    const params = new URLSearchParams({
      module: 'notificacao',
      id: notifMatch[1],
      redirect: fullPath,
    });
    irParaLogin(`/compartilhado/login?${params.toString()}`);
    return;
  }

  irParaLogin('/login');
};

const registrarFalhaDeRefresh = (motivo: string, status?: number): void => {
  if (typeof window === 'undefined') return;

  void import('@datadog/browser-rum')
    .then(({ datadogRum }) => {
      datadogRum.addAction('auth.refreshFalhou', { motivo, status });
    })
    .catch(() => {});
};

const encerrarSessaoLocal = (): void => {
  stopAutoRefresh();
  removeLegacyAuthCookie();
  marcarSessaoInvalida();
  redirectToLoginIfNeeded();
};

export const iniciarSessaoLocal = (): void => {
  limparMarcaSessaoInvalida();
  failedRefreshCount = 0;
};

interface RequestOptions {
  endpoint: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: object | FormData;
  params?: Record<string, string | number | boolean>;
  headers?: Record<string, string>;
  responseType?: 'json' | 'blob';
  skipAuth?: boolean;
  signal?: AbortSignal;
  /**
   * Mantém a requisição viva depois de a página começar a fechar. Para registro que
   * a pessoa já considera feito — a nota da pesquisa, por exemplo, que o cartão
   * declara enviada no primeiro clique. Sem isto, fechar a aba no instante seguinte
   * ao clique aborta o envio e a resposta some. Limite do browser: 64 KB de corpo.
   */
  keepalive?: boolean;
  /**
   * `false` desliga a recuperação de workspace no 403 (refazer sem `X-Workspace-Id` e, se o 403
   * persistir, apagar o workspace guardado e recarregar). Serve à rota cujo 403 é a resposta de
   * permissão do próprio recurso e que exige o header: nela a segunda tentativa volta 400 por
   * falta do header, e o 400 esconde o 403 que a tela precisa mostrar. O padrão segue `true`.
   */
  recuperarWorkspaceNo403?: boolean;
}

export const refreshAccessToken = async (): Promise<boolean> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  if (failedRefreshCount >= MAX_REFRESH_ATTEMPTS) {
    encerrarSessaoLocal();
    return false;
  }

  refreshPromise = (async () => {
    try {
      const baseUrl = BASE_URL.replace(/\/$/, '');
      const refreshUrl = `${baseUrl}/auth/refresh`;

      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
        credentials: 'include',
        signal: AbortSignal.timeout(10000),
      });

      if (!response.ok) {
        registrarFalhaDeRefresh('respostaNaoOk', response.status);

        if (response.status === 401 || response.status === 403) {
          failedRefreshCount = MAX_REFRESH_ATTEMPTS;
          encerrarSessaoLocal();
          return false;
        }

        failedRefreshCount++;
        if (failedRefreshCount >= MAX_REFRESH_ATTEMPTS) {
          encerrarSessaoLocal();
        }
        return false;
      }

      failedRefreshCount = 0;
      ultimaRenovacaoEm = Date.now();
      return true;
    } catch {
      registrarFalhaDeRefresh('erroDeRede');
      failedRefreshCount++;
      if (failedRefreshCount >= MAX_REFRESH_ATTEMPTS) {
        encerrarSessaoLocal();
      }
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Renova ao voltar para a aba, e não só pelo timer.
 *
 * O `setInterval` sozinho não segura os 15 minutos do access token nos casos que mais aparecem no
 * uso real: o browser estrangula (e chega a congelar) timers de aba em segundo plano, e trabalho
 * longo na thread principal — gerar o PDF de um certificado de muitas páginas, por exemplo —
 * atrasa o disparo. Quando a aba volta a ficar visível é justamente quando a pessoa vai navegar,
 * então é o melhor instante para ter credencial fresca.
 *
 * O piso evita renovar a cada alt-tab: dentro dele o access token ainda está longe de vencer.
 */
const renovarAoVoltarParaAba = (): void => {
  if (document.visibilityState !== 'visible') return;
  if (Date.now() - ultimaRenovacaoEm < MIN_INTERVALO_ENTRE_RENOVACOES) return;
  void refreshAccessToken();
};

export const startAutoRefresh = () => {
  stopAutoRefresh();
  iniciarSessaoLocal();

  refreshIntervalId = setInterval(() => {
    void refreshAccessToken();
  }, AUTO_REFRESH_INTERVAL);

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', renovarAoVoltarParaAba);
  }
};

export const stopAutoRefresh = () => {
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }

  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', renovarAoVoltarParaAba);
  }
};

const API_ORIGIN = BASE_URL.replace(/\/$/, '').replace(/\/api$/, '');

const resolveApiUrl = (endpoint: string): string => {
  const baseUrl =
    typeof window !== 'undefined' && endpoint.startsWith('/api/')
      ? window.location.origin
      : BASE_URL.replace(/\/$/, '');
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${cleanEndpoint}`;
};

/**
 * Absolute URL for a path the backend itself returns already rooted at its
 * host and including the `/api` prefix (e.g. `documento.downloadUrl`).
 */
export const resolveBackendUrl = (absolutePath: string): string => {
  const path = absolutePath.startsWith('/') ? absolutePath : `/${absolutePath}`;
  return `${API_ORIGIN}${path}`;
};

/**
 * Ler e aceitar convite por token acontece antes de haver workspace escolhido — quem aceita ainda
 * não é membro de lugar nenhum. A GESTÃO de convites (`/convites-escritorio/escritorio/:id`) é o
 * oposto: o backend tira o escopo do header e, sem ele, respondia com os convites de outros
 * escritórios.
 */
const isConvitePorToken = (endpoint: string): boolean =>
  (endpoint.startsWith('/convites-escritorio/') &&
    !endpoint.startsWith('/convites-escritorio/escritorio/')) ||
  endpoint.startsWith('/escritorios/convites/');

export const request = async ({
  endpoint,
  method = 'GET',
  body,
  params,
  headers: customHeaders = {},
  responseType,
  skipAuth = false,
  signal,
  keepalive = false,
  recuperarWorkspaceNo403 = true,
}: RequestOptions): Promise<Response> => {
  const headers: Record<string, string> =
    responseType === 'blob'
      ? {
          Accept: 'application/pdf, image/*, application/octet-stream',
          ...customHeaders,
        }
      : !body
        ? {
            ...customHeaders,
          }
        : {
            'Content-Type': 'application/json',
            ...customHeaders,
          };

  const tenantDomain = process.env.NEXT_PUBLIC_TENANT_DOMAIN;
  if (tenantDomain && !headers['X-Tenant-Domain']) headers['X-Tenant-Domain'] = tenantDomain;

  if (typeof window !== 'undefined') {
    const ep = endpoint || '';
    // `/auth/me` é a exceção da exceção: o backend resolve `papelEscritorio` pelo workspace do
    // header e, sem ele, cai no escritório home (`Usuario.escritorioId`) — que é nulo para quem
    // só acessa workspace via EscritorioMembro. As demais rotas de auth (login, refresh,
    // verify-token) rodam antes de existir workspace escolhido.
    const isAuthEp = ep.startsWith('/auth') && ep !== '/auth/me';
    const isAdminEp = ep.startsWith('/admin');
    const isFunilPublicoEp = ep.startsWith('/negociacao-self') || ep.startsWith('/onboarding-self');
    const isArbitralSituacaoEp =
      ep.startsWith('/admin/arbitral/situacoes') ||
      (ep.startsWith('/admin/arbitral/processos') && ep.includes('/situacoes'));
    const isWsBootstrap =
      ep === '/escritorios/workspaces' ||
      ep === '/escritorios/pessoal' ||
      isConvitePorToken(ep) ||
      (method === 'GET' && /^\/escritorios\/[0-9a-fA-F-]{36}$/.test(ep));
    if ((!isAuthEp && !isAdminEp && !isWsBootstrap && !isFunilPublicoEp) || isArbitralSituacaoEp) {
      const wsId = getStoredWorkspaceId();
      if (wsId) headers['X-Workspace-Id'] = wsId;
    }
  }

  Object.assign(headers, await headersDeContextoAuditoria(method));

  let finalBody: BodyInit | null = null;
  if (body) {
    if (body instanceof FormData) {
      delete headers['Content-Type'];
      finalBody = body;
    } else {
      finalBody = JSON.stringify(body);
    }
  }

  const url = new URL(
    resolveApiUrl(endpoint),
    typeof window !== 'undefined' ? window.location.origin : undefined,
  );

  if (params) {
    Object.entries(params).forEach(([key, value]) => url.searchParams.append(key, String(value)));
  }

  let response = await fetch(url.toString(), {
    method,
    headers,
    body: method !== 'GET' ? finalBody : undefined,
    credentials: 'include',
    signal,
    keepalive,
  });

  if (response.status === 401 && !skipAuth) {
    const refreshed = await refreshAccessToken();

    if (refreshed) {
      response = await fetch(url.toString(), {
        method,
        headers,
        body: method !== 'GET' ? finalBody : undefined,
        credentials: 'include',
        signal,
        keepalive,
      });
    } else {
      encerrarSessaoLocal();
      throw new Error('Sessão expirada');
    }
  }

  if (typeof window !== 'undefined' && headers['X-Workspace-Id'] && response.status !== 403) {
    window.sessionStorage.removeItem(WS_RECOVERY_ATTEMPTS_KEY);
  }

  if (
    recuperarWorkspaceNo403 &&
    response.status === 403 &&
    typeof window !== 'undefined' &&
    headers['X-Workspace-Id']
  ) {
    const wsRecoveryKey = 'wsRecoveryInProgress';
    const now = Date.now();

    const lastRecoveryAt = Number(window.sessionStorage.getItem(wsRecoveryKey) || '0');
    const shouldRecover = !lastRecoveryAt || now - lastRecoveryAt > 10_000;

    if (shouldRecover) {
      window.sessionStorage.setItem(wsRecoveryKey, String(now));

      const retryHeaders: Record<string, string> = { ...headers };
      delete retryHeaders['X-Workspace-Id'];

      const retryResponse = await fetch(url.toString(), {
        method,
        headers: retryHeaders,
        body: method !== 'GET' ? finalBody : undefined,
        credentials: 'include',
        signal,
        keepalive,
      });

      if (retryResponse.status !== 403) {
        return retryResponse;
      }

      const attempts = Number(window.sessionStorage.getItem(WS_RECOVERY_ATTEMPTS_KEY) || '0') + 1;
      window.sessionStorage.setItem(WS_RECOVERY_ATTEMPTS_KEY, String(attempts));
      if (attempts > WS_RECOVERY_MAX_ATTEMPTS) {
        return retryResponse;
      }

      try {
        removeStoredWorkspaceId();
      } catch {}
      window.location.reload();
    }
  }

  return response;
};

export const apiGet = (
  endpoint: string,
  params?: Record<string, string | number | boolean>,
  skipAuth = false,
  signal?: AbortSignal,
) => request({ endpoint, method: 'GET', params, skipAuth, signal });

export const apiPost = (
  endpoint: string,
  body?: object | FormData,
  skipAuth = false,
  signal?: AbortSignal,
  keepalive = false,
) => request({ endpoint, method: 'POST', body, skipAuth, signal, keepalive });

export const apiPut = (endpoint: string, body?: object, skipAuth = false, signal?: AbortSignal) =>
  request({ endpoint, method: 'PUT', body, skipAuth, signal });

export const apiDelete = (endpoint: string, skipAuth = false, signal?: AbortSignal) =>
  request({ endpoint, method: 'DELETE', skipAuth, signal });

export const apiPatch = (
  endpoint: string,
  body?: object,
  skipAuth = false,
  signal?: AbortSignal,
  keepalive = false,
) => request({ endpoint, method: 'PATCH', body, skipAuth, signal, keepalive });

const apiGetBlob = (
  endpoint: string,
  params?: Record<string, string | number | boolean>,
  signal?: AbortSignal,
) => request({ endpoint, method: 'GET', params, responseType: 'blob', signal });

export const downloadBlob = async (endpoint: string, filename: string, signal?: AbortSignal) => {
  const response = await apiGetBlob(endpoint, undefined, signal);
  if (!response.ok) throw new Error('Erro ao baixar arquivo');

  salvarBlobComoArquivo(await response.blob(), filename);
};

export const getBlobObjectUrl = async (endpoint: string): Promise<string> => {
  const response = await apiGetBlob(endpoint);
  if (!response.ok) throw new Error('Erro ao carregar arquivo');

  const blob = await response.blob();
  return window.URL.createObjectURL(blob);
};

export const visualizarBlob = async (endpoint: string) => {
  const url = await getBlobObjectUrl(endpoint);
  window.open(url, '_blank');
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};
